import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errorMessage } from "@/lib/api";
import { MONTHS, formatDate, setServiceTypes, todayISO } from "@/lib/services";
import { ServicesContext } from "@/context/ServicesContext";
import { TopBar } from "@/components/app/TopBar";
import { StatsStrip } from "@/components/app/StatsStrip";
import { Legend } from "@/components/app/Legend";
import { YearCalendar } from "@/components/app/YearCalendar";
import { MonthSheet } from "@/components/app/MonthSheet";
import { ServiceList } from "@/components/app/ServiceList";
import { AlertsView } from "@/components/app/AlertsView";
import { ClientsView } from "@/components/app/ClientsView";
import { ServiceTypesView } from "@/components/app/ServiceTypesView";
import { PlanningView } from "@/components/app/PlanningView";
import { MessagesView } from "@/components/app/MessagesView";
import { ArchivedView } from "@/components/app/ArchivedView";
import { CalendarFilters, EMPTY_FILTER, applyFilter } from "@/components/app/CalendarFilters";
import { RevenueDashboard } from "@/components/app/RevenueDashboard";
import { WeekRoute } from "@/components/app/WeekRoute";
import { ClientHistorySheet } from "@/components/app/ClientHistorySheet";
import { ServiceFormDialog } from "@/components/app/ServiceFormDialog";
import { DeleteDialog } from "@/components/app/DeleteDialog";
import { CompanyDialog } from "@/components/app/CompanyDialog";
import { TeamDialog } from "@/components/app/TeamDialog";
import { BillingDialog } from "@/components/app/BillingDialog";
import { TrialBanner } from "@/components/app/TrialBanner";
import { VerifyEmailBanner } from "@/components/app/VerifyEmailBanner";
import { RenewalReminder } from "@/components/app/RenewalReminder";

const TABS = [
  ["calendario", "Calendário"], ["dashboard", "Dashboard"], ["planejamento", "Planejamento"], ["semana", "Semana"], ["mes", "Mês atual"], ["proximos", "Próximos"], ["alertas", "Alertas"], ["arquivados", "Arquivados"], ["clientes", "Clientes"], ["mensagens", "Mensagens"], ["catalogo", "Catálogo"],
];

export default function Dashboard() {
  const today = todayISO();
  const [year, setYear] = useState(Number(today.slice(0, 4)));
  const [tab, setTab] = useState(() => {
    const aba = new URLSearchParams(window.location.search).get("aba");
    return TABS.some(([v]) => v === aba) ? aba : "calendario";
  });
  const [version, setVersion] = useState(0);
  const [services, setServices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [month, setMonth] = useState(null);
  const [clientName, setClientName] = useState(null);
  const [company, setCompany] = useState(null);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const [contacts, setContacts] = useState({});
  const [messaging, setMessaging] = useState(null);
  const [calFilter, setCalFilter] = useState(EMPTY_FILTER);
  const shown = useMemo(() => applyFilter(services, calFilter, today), [services, calFilter, today]);

  useEffect(() => {
    api.get("/settings/company").then((r) => setCompany(r.data)).catch(() => {});
  }, []);

  const bump = useCallback(() => setVersion((v) => v + 1), []);
  const [typesVersion, setTypesVersion] = useState(0);

  useEffect(() => {
    api.get("/settings/messaging").then((r) => setMessaging(r.data)).catch(() => {});
  }, [version]);

  const complete = useCallback(async (s) => {
    try {
      const { data } = await api.post(`/services/${s.id}/complete`, {});
      toast.success(`Realizado em ${formatDate(s.execution_date)}.${data.next ? ` Próximo agendado para ${formatDate(data.next.execution_date)}.` : ""}`);
      bump();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }, [bump]);

  useEffect(() => {
    api.get("/service-types").then((r) => { setServiceTypes(r.data); setTypesVersion((v) => v + 1); }).catch((e) => toast.error(errorMessage(e)));
  }, [version]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has("assinatura")) return;
    setBillingOpen(true);
    window.dispatchEvent(new Event("auth:reload"));
    window.history.replaceState(null, "", window.location.pathname);
  }, []);

  useEffect(() => {
    api.get("/services", { params: { year } }).then((r) => setServices(r.data)).catch((e) => toast.error(errorMessage(e)));
  }, [year, version]);

  useEffect(() => {
    api.get("/clients").then((r) => setContacts(Object.fromEntries(r.data.map((c) => [c.key, c])))).catch(() => {});
  }, [version]);

  useEffect(() => {
    api.get("/dashboard", { params: { today } }).then((r) => setSummary(r.data)).catch((e) => toast.error(errorMessage(e)));
  }, [today, version]);

  const ctx = useMemo(() => ({
    today, bump,
    openNew: (defaults = {}) => setForm({ defaults }),
    openEdit: (entry) => setForm({ entry }),
    openComplete: complete,
    requestDelete: setDeleting,
    openClient: setClientName,
    company,
    contacts,
    messaging,
    openCompany: () => setCompanyOpen(true),
    openTeam: () => setTeamOpen(true),
    openBilling: () => setBillingOpen(true),
  }), [today, bump, company, contacts, messaging, complete]);

  const currentMonthName = `${MONTHS[Number(today.slice(5, 7)) - 1]} ${today.slice(0, 4)}`;

  return (
    <ServicesContext.Provider value={ctx}>
      <div className="app-grain min-h-screen">
        <TopBar year={year} setYear={setYear} />
        <main className="mx-auto max-w-[1500px] space-y-6 px-4 pb-16 pt-6 sm:px-6 lg:px-10">
          <VerifyEmailBanner />
          <TrialBanner />
          <RenewalReminder />
          <StatsStrip summary={summary} monthName={currentMonthName} onSelect={setTab} />
          <Tabs value={tab} onValueChange={setTab}>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <TabsList className="h-auto flex-wrap justify-start gap-1 bg-slate-900/80 p-1">
                {TABS.map(([value, label]) => (
                  <TabsTrigger key={value} value={value} data-testid={`tab-${value}`} className="rounded-md px-4 py-2 data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900">
                    {label}
                    {value === "arquivados" && summary?.stats?.archived > 0 && (
                      <span className={`ml-1.5 rounded-full px-1.5 font-mono text-[11px] ${summary.stats.archived_reminders ? "bg-amber-400 text-amber-950" : "bg-slate-700 text-slate-200"}`} data-testid="tab-arquivados-count">{summary.stats.archived}</span>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>
              <Legend />
            </div>
            <TabsContent value="calendario" className="mt-6">
              <CalendarFilters f={calFilter} setF={setCalFilter} shown={shown.length} total={services.length} typesVersion={typesVersion} />
              <YearCalendar year={year} services={shown} today={today} onOpenMonth={setMonth} />
            </TabsContent>
            <TabsContent value="dashboard" className="mt-6">
              <RevenueDashboard key={year} services={services} year={year} today={today} onOpenMonth={(m) => { setCalFilter(EMPTY_FILTER); setMonth(m); }} />
            </TabsContent>
            <TabsContent value="semana" className="mt-6">
              <WeekRoute version={version} />
            </TabsContent>
            <TabsContent value="mes" className="mt-6">
              <ServiceList title={`Serviços de ${currentMonthName}`} items={summary?.current_month} empty="Nenhum serviço neste mês." testid="current-month-list" />
            </TabsContent>
            <TabsContent value="proximos" className="mt-6">
              <ServiceList title="Serviços futuros agendados" items={summary?.upcoming} empty="Nenhum serviço futuro agendado." testid="upcoming-list" grouped />
            </TabsContent>
            <TabsContent value="alertas" className="mt-6">
              <AlertsView summary={summary} />
            </TabsContent>
            <TabsContent value="arquivados" className="mt-6">
              <ArchivedView version={version} onChanged={bump} />
            </TabsContent>
            <TabsContent value="clientes" className="mt-6">
              <ClientsView version={version} />
            </TabsContent>
            <TabsContent value="planejamento" className="mt-6">
              <PlanningView version={version} onChanged={bump} />
            </TabsContent>
            <TabsContent value="mensagens" className="mt-6">
              <MessagesView version={version} onChanged={bump} />
            </TabsContent>
            <TabsContent value="catalogo" className="mt-6">
              <ServiceTypesView version={typesVersion} onChanged={bump} />
            </TabsContent>
          </Tabs>
        </main>
      </div>
      <MonthSheet month={month} year={year} services={shown} onClose={() => setMonth(null)} onMonth={setMonth} />
      <ClientHistorySheet name={clientName} version={version} onClose={() => setClientName(null)} />
      <ServiceFormDialog state={form} onClose={() => setForm(null)} />
      <DeleteDialog entry={deleting} onClose={() => setDeleting(null)} />
      <CompanyDialog open={companyOpen} company={company} onClose={() => setCompanyOpen(false)} onSaved={setCompany} />
      <TeamDialog open={teamOpen} onClose={() => setTeamOpen(false)} />
      <BillingDialog open={billingOpen} onClose={() => setBillingOpen(false)} />
    </ServicesContext.Provider>
  );
}
